import { db } from '../firebase_utils/firebase';
import { 
  collection, 
  addDoc, 
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  increment
} from "firebase/firestore";
import { emitCreditsUpdated } from '../hooks/useCredits';

export const saveFeedback = async (feedbackData, userId = null, sessionId = null) => {
  try {
    // console.log('Saving feedback:', { userId, sessionId, ...feedbackData });
    
    const feedbackDoc = {
      rating: feedbackData.rating,
      feedbackType: feedbackData.feedbackType,
      feedbackText: feedbackData.feedbackText,
      timestamp: serverTimestamp()
    };

    if (userId) {
      feedbackDoc.userId = userId;
    } else if (sessionId) {
      feedbackDoc.sessionId = sessionId;
    }
    
    const docRef = await addDoc(collection(db, "feedback"), feedbackDoc);
    
    // console.log('Feedback saved with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    // console.error("Error saving feedback: ", error);
    throw error;
  }
};

export const createOrUpdateUser = async (user) => {
  try {
    const userRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // New user - create document with default tier
      await setDoc(userRef, {
        name: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        tier: 'free', // Default tier
        credits: 100, // Initial credits for new users
        totalCreditsEarned: 100, // Track lifetime credits
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      // console.log('Created new user document');
    }

    return userDoc;
  } catch (error) {
    // console.error("Error managing user document:", error);
    throw error;
  }
};

export const getUserTier = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return 'anonymous';
    }
    
    return userDoc.data()?.tier || 'anonymous';
  } catch (error) {
    // console.error("Error getting user tier:", error);
    return 'anonymous';
  }
};

/**
 * Get the current credit balance of a user
 * @param {string} userId - The user's ID
 * @returns {Promise<number>} Number of credits the user has
 */
export const getUserCredits = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return 0; // No user found, return 0 credits
    }
    
    return userDoc.data()?.credits || 0;
  } catch (error) {
    // console.error("Error getting user credits:", error);
    return 0; // Return 0 on error
  }
};

/**
 * Deduct credits from a user's account
 * @param {string} userId - The user's ID
 * @param {number} amount - Amount of credits to deduct
 * @param {string} feature - The feature for which credits are being deducted
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const deductCredits = async (userId, amount, feature) => {
  try {
    // First check if the user has enough credits
    const currentCredits = await getUserCredits(userId);
    
    if (currentCredits < amount) {
      return false; // Not enough credits
    }
    
    // Update the user's credits
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      credits: increment(-amount),
      updatedAt: serverTimestamp()
    });
    
    // Log the transaction
    await logCreditTransaction(userId, -amount, feature, "Used credits for feature");
    
    // Emit the credits updated event to update UI components
    emitCreditsUpdated();
    
    return true;
  } catch (error) {
    // console.error("Error deducting credits:", error);
    return false;
  }
};

/**
 * Log a credit transaction in the creditTransactions collection
 * @param {string} userId - The user's ID
 * @param {number} amount - Amount of credits (positive for added, negative for deducted)
 * @param {string} feature - The feature related to the transaction
 * @param {string} description - Description of the transaction
 * @returns {Promise<string>} ID of the transaction document
 */
export const logCreditTransaction = async (userId, amount, feature, description) => {
  try {
    const transactionData = {
      userId,
      amount,
      feature,
      description,
      timestamp: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, "creditTransactions"), transactionData);
    return docRef.id;
  } catch (error) {
    // console.error("Error logging credit transaction:", error);
    throw error;
  }
};